import {sortBy, difference, uniq} from 'lodash-es';
import DebouncedToggle from '../../../components/debounced-toggle';

import * as SearchActions from '../../../state/search/search.action';
import template from './filters.component.html';
import {mapFilter} from './attribute-filter/attribute-filter.models';

export const noNulls = item => item != null && item !== '';
export const pluckValue = item => (item.value ? item.value : item);
export const isSelected = item => item._selected === true;

const adaptTagFilters = selectedFilters => {
  const filterObj = {};

  selectedFilters.forEach(item => {
    const rangeExists = !!item.selectedRange && (!!item.selectedRange.min || !!item.selectedRange.max);
    if (!rangeExists || item.anyValue) {
      const values = item.selectedValues || [];
      filterObj[item.key] = item.anyValue ? {any: true} : values.filter(noNulls).map(pluckValue);
    } else {
      filterObj[item.key] = adaptRange({max: item.selectedRange.max, min: item.selectedRange.min});
    }
  });

  return filterObj;
};

const adaptRange = selectedRange => {
  const newRange = {};
  if (selectedRange) {
    if (selectedRange.min) {
      newRange.min = selectedRange.min;
    }

    if (selectedRange.max) {
      newRange.max = selectedRange.max;
    }
  }

  return newRange;
};

class AssetFiltersController extends DebouncedToggle {
  constructor($ngRedux, $timeout) {
    'ngInject';

    super($timeout);

    this.store = $ngRedux;

    this.communicatedLabel = 'Communicated';
    this.selectedAttr = null;
    this.selectedAttrFilters = this.selectedSecurityGroups = [];
    this.showModal = false;
  }

  $onInit() {
    this.unsubscribe = this.store.connect(this.mapStateToThis, SearchActions)(this);
  }

  $onDestroy() {
    this.unsubscribe();
  }

  toggleModal() {
    this.showModal = !this.showModal;
  }

  mapStateToThis(state) {
    const currentState = state;
    const {tagFilters} = currentState.search;
    let {securityGroups, accounts, lastSeen, lastCommunicated} = currentState.search;

    securityGroups = securityGroups.map(g => ({
      ...g,
      label: g.name
    }));

    let selectedSecurityGroups = securityGroups.filter(isSelected);
    securityGroups = difference(securityGroups, selectedSecurityGroups);

    const selectedAccounts = accounts.filter(isSelected);
    const unSelectedAccounts = difference(accounts, selectedAccounts);

    const {items} = state.attributes;
    const allAttrFilters = items.map(mapFilter);

    let attrFilters = [];
    let selectedAttrFilters = [];

    Object.keys(tagFilters).forEach(key => {
      const filter = allAttrFilters.find(attrFilter => attrFilter.key.toLowerCase() === key.toLowerCase());
      if (filter) {
        const {any, min, max} = tagFilters[key];

        let selectedValues = [];
        if (!min && !max) {
          const filterValues = any ? [] : tagFilters[key];
          selectedValues = filterValues.filter(noNulls);
        }

        selectedAttrFilters.push({
          ...filter,
          _selected: true,
          anyValue: !!any,
          selectedRange: adaptRange({min, max}),
          selectedValues: [...selectedValues]
        });
      }
    });

    allAttrFilters.forEach(prop => {
      const filter = selectedAttrFilters.find(filterItem => filterItem.key === prop.key);
      if (!filter) {
        attrFilters.push({...prop, _selected: false});
      }
    });

    attrFilters = sortBy(attrFilters, 'label');
    selectedAttrFilters = sortBy(selectedAttrFilters, 'label');
    securityGroups = sortBy(securityGroups, 'label');
    selectedSecurityGroups = sortBy(selectedSecurityGroups, 'label');

    const [lastSeenFilter] = lastSeen || [];

    return {
      securityGroups,
      selectedSecurityGroups,
      attrFilters,
      selectedAttrFilters,
      accounts: unSelectedAccounts,
      selectedAccounts,
      lastSeenFilter,
      lastCommunicated
    };
  }

  updateSecurityGroupFacet(group) {
    if (!group._selected) {
      this.selectFacet(SearchActions.SECURITY_GROUPS_FACET_TYPE, group);
    } else {
      this.deselectFacet(SearchActions.SECURITY_GROUPS_FACET_TYPE, group);
    }
  }

  addAttrFilter(item) {
    this.selectedAttr = item;
  }

  applyAttrFilter(filter) {
    if (filter) {
      let existingFilters = [...this.selectedAttrFilters];
      let selectedFilter = existingFilters.find(existingFilter => existingFilter.id === filter.id);
      if (!selectedFilter) {
        selectedFilter = {...filter};
        existingFilters.push(selectedFilter);
      }

      if (!filter.anyValue) {
        selectedFilter.values = filter.values;
        selectedFilter.selectedValues = uniq(filter.selectedValues);
        selectedFilter.selectedRange = adaptRange(filter.selectedRange);
      }

      const adaptedFilters = existingFilters && existingFilters.length > 0 ? adaptTagFilters(existingFilters) : [];

      this.updateTagFilters(adaptedFilters, true);
    }
  }

  removeAttrFilter(attr) {
    const filtered = this.selectedAttrFilters.filter(item => {
      const keep = item.id !== attr.id;
      if (!keep) {
        item.applied = false;
        item._selected = false;
      }
      return keep;
    });

    const adaptedFilters = filtered && filtered.length > 0 ? adaptTagFilters(filtered) : [];

    this.updateTagFilters(adaptedFilters, true);
  }

  clearSelected() {
    this.closeModal();
    this.selectedAttr = null;

    // trim invalid inputs
    [this.selectedAttrFilters, this.attrFilters].forEach(filterGroup => {
      filterGroup.forEach(filter => {
        const currentSelectedValues = filter.selectedValues ? filter.selectedValues : [];
        if (currentSelectedValues.length > 0) {
          filter.selectedValues = currentSelectedValues.filter(noNulls);
        }
      });
    });
  }

  _preventBubbling($event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  toggleAdvancedFilters($event) {
    this._preventBubbling($event);
    this._debounceToggle()
      .then(() => (this.showAdvancedFilters = !this.showAdvancedFilters))
      .catch(this._ignore);
  }

  hideAdvanced() {
    this._debounceToggle()
      .then(() => (this.showAdvancedFilters = false))
      .catch(this._ignore);
  }

  addAccount(account) {
    this.addAccount(account);
  }

  removeAccount(account) {
    this.removeAccount(account);
  }

  updateLastSeenOption(option) {
    this.updateLastSeen(option);
  }
  onSelectCommunicatedAt(option = {}) {
    this.exclusiveSelectFacet(SearchActions.COMMUNICATED_AT_FACET_TYPE, option);
  }
  onClearCommunicatedAt(option = {}) {
    this.exclusiveDeselectFacet(SearchActions.COMMUNICATED_AT_FACET_TYPE, option);
  }
}

export const assetFiltersComponent = {
  template,
  controller: AssetFiltersController,
  controllerAs: 'vm',
  bindings: {
    closeFilters: '&',
    closeModal: '&'
  }
};
